import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLOListElement} [dotsList]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The dot buttons built for the currently rendered slides.
   * @type {HTMLButtonElement[]}
   */
  #dots = [];

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerdown', this.#handlePointerDown);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#buildDots();

    // Force a consistent slide/dot state up front, regardless of what was
    // rendered server-side - the number of slides that actually render can
    // differ from the configured block count (e.g. blocks hidden via a
    // visibility setting).
    this.current = 0;

    // Only the slides actually rendered by Liquid count toward navigation -
    // hide controls entirely when there's nothing to navigate between.
    if ((this.refs.slides ?? []).length <= 1) {
      this.refs.dotsList?.setAttribute('hidden', '');
      this.querySelector('slideshow-arrows')?.setAttribute('hidden', '');
    } else {
      this.play();
    }
  }

  /**
   * Builds one dot per rendered slide, since the number of slides that
   * actually render can differ from the configured block count (e.g. blocks
   * hidden via a visibility setting).
   */
  #buildDots() {
    const container = this.refs.dotsList;
    const slides = this.refs.slides ?? [];

    if (!container || slides.length === 0) return;

    const labelTemplate = container.dataset.labelTemplate ?? '';

    this.#dots = slides.map((_, index) => {
      const li = document.createElement('li');
      const button = document.createElement('button');

      button.className = 'announcement-bar__dot button-unstyled';
      button.setAttribute('on:click', `/select/${index}`);
      button.textContent = `${index + 1}`;
      button.setAttribute(
        'aria-label',
        labelTemplate.replace('__INDEX__', `${index + 1}`).replace('__LENGTH__', `${slides.length}`)
      );

      li.appendChild(button);
      return button;
    });

    container.replaceChildren(...this.#dots.map((button) => button.parentElement));
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  select(index) {
    this.current = parseInt(index, 10);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      const isHidden = index !== relativeIndex;
      slide.setAttribute('aria-hidden', `${isHidden}`);
      slide.inert = isHidden;
    });

    this.#dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', `${index === relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  /**
   * Starts tracking a pointer (mouse drag or touch swipe) to allow navigating
   * between slides by swiping, mirroring the click/dot controls.
   * @param {PointerEvent} event - The pointerdown event.
   */
  #handlePointerDown = (event) => {
    if ((this.refs.slides ?? []).length <= 1) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const startX = event.clientX;
    const startY = event.clientY;
    const controller = new AbortController();
    const { signal } = controller;
    let moved = false;

    this.suspend();

    const onPointerMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      if (!moved && Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) {
        moved = true;
        this.setPointerCapture(moveEvent.pointerId);
        document.addEventListener('click', preventDefault, { once: true, signal });
      }
    };

    const onPointerUp = (upEvent) => {
      controller.abort();

      if (moved) {
        const deltaX = upEvent.clientX - startX;

        if (Math.abs(deltaX) > 40) {
          if (deltaX < 0) {
            this.next();
          } else {
            this.previous();
          }
        }

        this.releasePointerCapture(upEvent.pointerId);
      }

      this.resume();
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  };
}

/**
 * Prevents the default action of an event, used to suppress a click that
 * follows a swipe gesture.
 * @param {Event} event
 */
function preventDefault(event) {
  event.preventDefault();
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
